import { Component, OnInit, AfterViewInit, ChangeDetectorRef, OnDestroy, input, Renderer2 } from '@angular/core';
import { StepDefinition } from '../dynamic';
import { FieldType, FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { ContextService, ServerServiceConfig } from '../../context.service';
import { Subscription } from 'rxjs';

const emptyArray: any[] = [null, undefined, ''];
@Component({
  selector: 'mutara-review',
  templateUrl: './review-form.component.html',
  styleUrls: ['./review-form.component.scss'],
  standalone: false
})
export class ReviewFormComponent extends FieldType<FieldTypeConfig> implements OnInit, AfterViewInit, OnDestroy {
  readonly mainModel = input<any>(undefined);

  fullyLoaded: boolean = false;
  isWorking: boolean = false;
  hasProcessedWaiver: boolean = false;
  appDefinition: ServerServiceConfig | null = null;
  appId: string | null = null;
  subscriptions: Subscription[] = [];

  constructor(private context: ContextService, private changeDetectorRef: ChangeDetectorRef, private renderEngine: Renderer2) {
    super();
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach( sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  get localModel(): StepDefinition[] {
    return this.props.dataModel;
  }

  get isValid(): boolean {
    return this.invalidCount == 0;
  }

  get debug(): boolean {
    return this.props?.capForm?.debug;
  }

  get errorInfo(): string {
    return this.formState.ui.getValue('review', 'mad_person_profile-status_text');
  }
  /** 
   * Perform a little magic to not include ourselves in the review by 
   * duplicating the array and removing our step from the new copy. 
   */
  ngOnInit() {
    this.subscriptions.push(this.context.subscribeTo("applicationInfo", (app: ServerServiceConfig) => this.appDefinition = app));
    this.subscriptions.push(this.context.subscribeTo("applicationNumber", (id: string) => {
      this.appId = id;
      this.props.hidden = !['NEW', 'READY','PROCESS', 'QUEUE', 'ERROR'].includes('id');
    }));
  }

  ngAfterViewInit() {
    this.context.setApplicationNumber(this.formState.ui.getValue('review', 'mad_person_profile-status'));
    this.subscriptions.push(this.context.subscribeTo("readyToReceive", (isLoaded: boolean) => {
      if (!!(this.fullyLoaded = isLoaded)) {
        // Use the formChanges for the pages to check value validity
       this.field.props.dataModel.forEach((step: any) => this.checkValidFields(step));
       this.calculatePercentComplete();
        // Now setup for changes
        this.field.props.dataModel.forEach((layoutForm: StepDefinition) => {
          this.subscriptions.push(layoutForm.domForm.valueChanges.subscribe(() => {
            this.field.props.dataModel.forEach((step: any) => this.checkValidFields(step));
              this.calculatePercentComplete();
              this.changeDetectorRef.markForCheck();
          }));
        });
      }
    }));
  }

  get invalidCount() {
    let count = 0;
    this.props.dataModel.forEach((form: any) => (count += form?.invalidValues.length));
    return count;
  }
  /**
   * Check the fields values to display in review pane
   */
  checkValidFields(step: StepDefinition) {
    // Walk the current data and check for field validity
    //if ( this.debug ) { console.log('Check invalid %O', step.step);}
    step.totalRequired = 0;
    step.invalidValues = this.findInvalidControls(step);

    this.context.setIsValid(
      {
        valid: (this.isValid && !this.props.readonly) &&  !['READY','PROCESS'].includes( this.appId ?? ''),
        label: (!['NEW', 'ERROR'].includes(this.appId ?? 'NEW') ? ( ['READY','PROCESS'].includes( this.appId ?? '') ? 'Sending Data...' : 'See Detail') : undefined)
      });
  }

  // The values changes update this value
  getInvalidControls(stepName: string): any[] {
    return [... (this.localModel.find(step => step.step == stepName)?.invalidValues ?? [])];
  }

  parentLabel(control: FormlyFieldConfig) {
    return control?.props?.parentGroup?.props?.label;
  }

  /** Calculate the percent complete based on the invalid fields */
  calculatePercentComplete(): void {
    let total: number = 0, invalid: number = 0;
    this.localModel.forEach(step => {
      if (step.invalidValues) {
        total += step.totalRequired ?? 0;
        invalid += step.invalidValues?.length ?? 0;
      }
    });
    if (total > 0) {
      this.context.setPercentComplete(Math.round((total - invalid) * 100 / total));
    }
  }

  /**
   * The buttons should highlight the control and switch to the tab
   * @param index 
   * @param control 
   */
  setFocus(fieldConfig: FormlyFieldConfig) {
    this.context.setStepIndex(this.context.layout, fieldConfig.props?.stepName, fieldConfig, this.renderEngine); 
  }

  _processErrors(currentConfig: FormlyFieldConfig) {
    const currentControl = currentConfig.formControl;
    Object.keys(currentControl?.errors ?? []).forEach(key => {
      if (key.startsWith('__')) {
        return;
      }
      let error = currentControl?.errors?.[key], errorText = '';
      switch (typeof error) {
        case 'boolean':
          let useLabel = (!!currentConfig.props?.reviewLabel ? currentConfig.props?.reviewLabel : currentConfig.props?.label) ?? currentConfig.props?.parentGroup.props.label;
          errorText = useLabel.substring(0, 25) + (useLabel.length > 25 ? '...' : '') + ' is required';
          break;
        case 'object':
          if (typeof error.message == 'function') {
            errorText = error.message(error, currentConfig);
          } else if (!!error.message) {
            errorText = error.message;
          } else if (!!error.text) {
            if (!!error.text.toLocaleDateString) {
              errorText = `${currentConfig.props?.label} ${error.text.toLocaleDateString()} is not a valid date`;
            } else {
              errorText = `${currentConfig.props?.label} ${error.text.toString()} is not a valid value`;
            }
          } else if ( !!error.requiredMask ) {
            errorText = `${currentConfig.props?.label} (${error.actualValue}) is not a valid phone number`;
          } else {
            if (key.indexOf('Datepicker') > -1) {
              let thisDate = new Date(currentControl?.value);
              let fullDate = thisDate.getMonth() + '/' + thisDate.getDate() + '/' + thisDate.getFullYear();
              errorText = `${currentConfig.props?.label} ${fullDate} is out of date range`;
            } else {
              errorText = `${currentConfig.props?.label} ${JSON.stringify(error)} is invalid`;
            }
          }
          break;
        default:
          errorText = error;
          break;
      }
      if (currentConfig.props) { currentConfig.props.currentError = errorText + ' '; }
    });
    if (!!currentConfig.props?.currentError) {
      if (currentConfig.props.currentError.trim() == '') {
        currentConfig.props.currentError = 'Field is required to have a value';
      }
    }
  }

  /**
   * interate over the controls to find controls that are invalid 
   * @param stepDefinition
   * @param controlObject 
   */

  findInvalidControls(stepDefinition: StepDefinition): any[] {

    if (!!this.appDefinition?.acceptWaiver && !this.hasProcessedWaiver) {
      // Check at this point whether any waivers are valid
      this.subscriptions.push(this.context.subscribeTo("waiverResult", (result: boolean) => {
        this.formState.data[this.props.stepName]['mad_person_profile-extra.waiver_valid'] = result;
        this.changeDetectorRef.markForCheck();
      }));
      this.context.validateWaiver();
      this.hasProcessedWaiver = true;
    }
    // All of the fields will have the formlyFieldConfigs as part of the control object
    let stack = [...(stepDefinition.fieldDefinitions ?? [])], currentConfig: FormlyFieldConfig | undefined, currentControl: any, invalidList = [];
    stepDefinition.totalRequired = 0;
    while (!!(currentConfig = stack.shift())) {
      if (!(<string>currentConfig.key).endsWith('_add')) {
        try {
          // This is the control associated with the config, if not found we are in an odd/early state
          if (!!currentConfig.formControl) {
            //  console.error('Control not found in stack! %O step %O fields %O', currentConfig, stack, stepDefinition.fieldDefinitions);
            currentControl = currentConfig.formControl;
            if (currentConfig.props) { currentConfig.props.currentError = ''; }

            let hidden = !!currentConfig.props?.hidden; // && typeof currentConfig.expressions.hide == 'function' ? (<any>currentConfig.expressions).hide(currentConfig) : currentConfig.hide);
            // If it is a group, either check the precision (if there is one), or check the fields
            if (!!currentConfig?.fieldGroup && !hidden) {
              // If no precision this is not a table, add the children
              if (currentControl?.precision === undefined) { // These are just groups of fields, add the fields
                if (currentConfig.props?.paymentData !== undefined && !!currentControl.errors) {
                  currentConfig.props.currentError = Object.values(currentControl.errors).join('\n');
                  invalidList.push(currentConfig);
                }
                currentConfig.fieldGroup.forEach(field => stack.push(field));
              } else if ((currentControl?.precision ?? 0) > (currentControl?.size ?? 0)) { // This maps groups that happen to be tables and too small
                if (!!currentConfig.props?.isRequired) {
                  stepDefinition.totalRequired += (currentControl?.precision - currentControl?.size);
                }
                if (currentConfig.props) { currentConfig.props.currentError = currentConfig.props?.label + ' too few values'; }
                invalidList.push(currentConfig);
              }
            } else { // Not a group
              // if required, count it 
              if (!!currentConfig.props?.isRequired && !hidden) {
                stepDefinition.totalRequired++;
              }
              // if invalid and required, display the control
              if ('columns' == <string>currentConfig.type && this.debug) {
                console.debug('%O(%O) => %O', currentConfig.key, currentConfig.type, currentControl?.invalid);
              }

              if (!hidden &&
                (currentControl?.invalid ||
                  (!currentControl?.pending && !!currentConfig.props?.isRequired && emptyArray.includes(currentControl?.value)))) {
                // Here is where the error label is handled
                if (!!currentControl?.errors) {
                  this._processErrors(currentConfig);
                } else {
                  if (currentConfig.props) { currentConfig.props.currentError = currentConfig.props?.label + ' empty value'; }
                }
                invalidList.push(currentConfig);
              } else if (currentConfig.className != currentConfig.props?._oldClass) {
                currentConfig.className = currentConfig.props?._oldClass ?? null;
              }
            }
          }
        } catch (err) {
          console.error('Error pulling stack %O\n%O', currentConfig.key, err);
          break;
        }
      }
    }
    return invalidList;
  }

  formatError(text: string) {
    let error = text;
    if (!!text) {
      let errorLines = text.split('\n'), dt = errorLines.shift()?.split(' ')[0] ?? '0';
      error = `${(new Date(dt)).toLocaleDateString()}\n${errorLines.join('\n')?.split('  ')?.join('\n')}`;
    }
    return error;
  }

  /**
   * This sets the labels based on status 
   */
  get statusText() {
    let statusText = this.formState.ui.getValue('review', 'mad_person_profile-status');
    switch (statusText ?? 'NEW') {
      case 'NEW':
        statusText = (!!this.isValid ? 'Your application is ready to submit' : 'Your application needs attention');
        break;
      case 'PROCESS':
        statusText = 'Application is being processed';
        break;
      case 'ERROR':
        statusText = this.props.capForm.debug ? this.formatError(this.formState.ui.getValue('review', 'mad_person_profile-status_text')) : 'There was an error during the previous submission. Please review values and resubmit.';
        break;
      case 'FILEUPLOAD':
        statusText = 'Uploading supplied documentation to file management';
        break;
      default:
        statusText = 'Application Number ' + statusText;
        break;
    }
    return statusText;
  }
}

